import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  PawPrint,
  Beer,
  Scale,
  Search,
  Users,
  MapPin,
  Mail,
  Instagram,
  Globe,
  ArrowRight,
} from "lucide-react";
import { KILTRO_BRAND, KILTRO_ASSETS } from "../lib/assets";
import { FEATURE_FLAGS } from "../lib/featureFlags";

const VALUE_ICONS = [PawPrint, Beer, Scale, Users, Search];

export default function About() {
  const { t } = useTranslation();
  const values = [1, 2, 3, 4, 5].map((n, i) => ({
    icon: VALUE_ICONS[i],
    title: t(`about.values.v${n}Title`),
    desc: t(`about.values.v${n}Desc`),
  }));
  return (
    <div className="bg-black text-[#F5F5F5]" data-testid="about-page">
      {/* HERO / MANIFIESTO */}
      <section className="relative border-b-2 border-[#1A1A1A] overflow-hidden">
        <div className="max-w-5xl mx-auto px-4 lg:px-8 py-20 lg:py-24">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FEBA01] flex items-center gap-2">
            <PawPrint size={12} /> {t("about.eyebrow")}
          </div>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight leading-[1]">
            {t("about.title1")} <br />
            <span className="inline-block bg-[#FEBA01] text-black px-3 py-1 -skew-x-[6deg] mt-2 shadow-[4px_4px_0_0_rgba(0,0,0,0.6)]">
              {t("about.title2")}
            </span>
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-[#E5E5E5] leading-relaxed">
            {t("about.intro", { year: KILTRO_BRAND.founded, city: KILTRO_BRAND.city })}{" "}
            <span className="text-[#FEBA01]">{t("about.introHighlight")}</span>{t("about.introEnd")}
          </p>
          <p className="mt-5 max-w-2xl font-display text-2xl sm:text-3xl lg:text-4xl uppercase tracking-tight leading-[1.1] text-white">
            {t("about.manifestoQ")}
          </p>
          <p className="mt-6 max-w-2xl text-base text-[#A3A3A3] leading-relaxed">
            {t("about.afterQ")}
          </p>
        </div>
      </section>

      {/* MANIFIESTO / VALORES */}
      <section className="max-w-6xl mx-auto px-4 lg:px-8 py-20">
        <div className="max-w-2xl mb-12">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#FEBA01]">{t("about.codeEyebrow")}</div>
          <h2 className="mt-2 font-display text-4xl sm:text-5xl uppercase tracking-tight leading-[1]">
            {t("about.codeTitle1")} <br /> <span className="text-[#FEBA01]">{t("about.codeTitle2")}</span>
          </h2>
          <p className="mt-4 text-[#A3A3A3] text-base">{t("about.codeDesc")}</p>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {values.map((v, i) => (
            <div
              key={i}
              className={`relative bg-[#1A1A1A] border-4 p-6 hover:border-[#FEBA01] transition-colors ${
                i === 0 ? "border-[#FEBA01] md:col-span-2" : "border-[#333]"
              }`}
              data-testid={`about-value-${i}`}
            >
              <div className="absolute -top-4 -left-4 w-12 h-12 bg-[#FEBA01] text-black flex items-center justify-center font-display text-xl">
                0{i + 1}
              </div>
              <v.icon size={32} className="text-[#FEBA01] mb-4 mt-2" />
              <h3 className="font-display text-2xl uppercase tracking-tight leading-none">{v.title}</h3>
              <p className="mt-3 text-[#E5E5E5] leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA COMERCIAL */}
      <section className="bg-[#0F0F0F] border-t-2 border-[#1A1A1A]">
        <div className={`max-w-5xl mx-auto px-4 lg:px-8 py-20 grid gap-4 ${FEATURE_FLAGS.brandsPublic ? "md:grid-cols-2" : "md:grid-cols-1"}`}>
          <Link
            to="/locales"
            className="group bg-[#1A1A1A] border-2 border-[#FEBA01] p-8 hover:bg-[#FEBA01] hover:text-black transition"
            data-testid="about-cta-locales"
          >
            <Beer size={28} className="text-[#FEBA01] group-hover:text-black transition" />
            <div className="mt-3 font-display text-2xl uppercase tracking-tight">{t("about.ctaBar")}</div>
            <p className="mt-2 text-sm text-[#A3A3A3] group-hover:text-black transition">{t("about.ctaBarDesc")}</p>
            <div className="mt-4 inline-flex items-center gap-2 font-display uppercase text-sm">
              {t("about.ctaBarBtn")} <ArrowRight size={14} />
            </div>
          </Link>

          {FEATURE_FLAGS.brandsPublic && (
            <Link
              to="/marcas"
              className="group bg-[#1A1A1A] border-2 border-[#FEBA01] p-8 hover:bg-[#FEBA01] hover:text-black transition"
              data-testid="about-cta-marcas"
            >
              <MapPin size={28} className="text-[#FEBA01] group-hover:text-black transition" />
              <div className="mt-3 font-display text-2xl uppercase tracking-tight">{t("about.ctaBrand")}</div>
              <p className="mt-2 text-sm text-[#A3A3A3] group-hover:text-black transition">{t("about.ctaBrandDesc")}</p>
              <div className="mt-4 inline-flex items-center gap-2 font-display uppercase text-sm">
                {t("about.ctaBrandBtn")} <ArrowRight size={14} />
              </div>
            </Link>
          )}
        </div>
      </section>

      {/* CONTACTO / CIERRE */}
      <section className="border-t-2 border-[#1A1A1A]">
        <div className="max-w-4xl mx-auto px-4 lg:px-8 py-20 text-center">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#FEBA01]">{t("about.contactEyebrow")}</div>
          <h2 className="mt-3 font-display text-5xl sm:text-6xl uppercase tracking-tight leading-[0.9]">
            {t("about.contactTitle1")}
            <br />
            <span className="bg-[#FEBA01] text-black inline-block px-3 py-1 mt-3 -skew-x-[6deg]">
              {t("about.contactTitle2")}
            </span>
          </h2>
          <p className="mt-6 text-[#A3A3A3] max-w-xl mx-auto">{t("about.contactDesc")}</p>

          <div className="mt-8 grid sm:grid-cols-3 gap-3 max-w-3xl mx-auto">
            <ContactBlock icon={Mail} label={t("about.contactEmail")} value={KILTRO_BRAND.contactEmail} href={`mailto:${KILTRO_BRAND.contactEmail}`} testid="about-contact-email" />
            <ContactBlock icon={Instagram} label={t("about.contactIG")} value="@kiltroscout" href="https://instagram.com/kiltroscout" testid="about-contact-ig" />
            <ContactBlock icon={Globe} label={t("about.contactWeb")} value={KILTRO_BRAND.domain} href={`https://www.${KILTRO_BRAND.domain}`} testid="about-contact-web" />
          </div>

          <div className="mt-10">
            <img
              src={KILTRO_ASSETS.logo}
              alt="Kiltro"
              className="w-20 h-20 object-contain mx-auto opacity-90"
            />
            <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.3em] text-[#666]">
              {t("about.madeWith")} {KILTRO_BRAND.city}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function ContactBlock({ icon: Icon, label, value, href, testid }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="bg-[#1A1A1A] border-2 border-[#333] p-5 hover:border-[#FEBA01] transition group block overflow-hidden"
      data-testid={testid}
    >
      <Icon size={18} className="text-[#FEBA01] mx-auto group-hover:scale-110 transition" />
      <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-[#A3A3A3]">{label}</div>
      <div className="mt-1 font-mono text-[11px] sm:text-xs text-[#FEBA01] leading-snug" style={{ overflowWrap: "anywhere", wordBreak: "normal" }}>
        {value}
      </div>
    </a>
  );
}
